<template>
  <Teleport to="body">

    <div
      class="question-modal-overlay"
      @click.self="close"
    >

      <section
        class="question-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="question-modal-title"
      >

        <!-- =====================================================
             HEADER
        ====================================================== -->

        <header class="modal-header">

          <div class="header-copy">

            <div class="header-label">
              <span class="header-label-dot"></span>

              Обсуждение
            </div>


            <h2
              id="question-modal-title"
              class="modal-title"
            >
              {{ question.title || "Вопрос" }}
            </h2>


            <div class="header-meta">
              {{ answersCountTitle }}
            </div>

          </div>


          <button
            type="button"
            class="close-button"
            aria-label="Закрыть"
            @click="close"
          >

            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 7L17 17"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />

              <path
                d="M17 7L7 17"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

          </button>

        </header>


        <!-- =====================================================
             SCROLL CONTENT
        ====================================================== -->

        <div class="modal-scroll">

          <!-- ===================================================
               QUESTION
          ==================================================== -->

          <article class="question-main-card">

            <div class="question-accent"></div>


            <div class="question-main-label">

              <div class="question-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6.5 4.5H17.5C19.43 4.5 21 6.07 21 8V14C21 15.93 19.43 17.5 17.5 17.5H12L7.5 20.5V17.5H6.5C4.57 17.5 3 15.93 3 14V8C3 6.07 4.57 4.5 6.5 4.5Z"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linejoin="round"
                  />

                  <path
                    d="M9.6 9.2C9.8 8.05 10.65 7.3 11.95 7.3C13.35 7.3 14.2 8.15 14.2 9.15C14.2 10.85 12.1 10.85 12.1 12.35"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />

                  <circle
                    cx="12"
                    cy="14.5"
                    r="0.8"
                    fill="currentColor"
                  />
                </svg>

              </div>

              <span>
                Вопрос автора
              </span>

            </div>


            <p class="question-text">
              {{
                question.description
                || "Описание вопроса отсутствует."
              }}
            </p>


            <!-- AUTHOR -->

            <div class="author-row">

              <div class="avatar-wrapper">

                <img
                  v-if="question.creatorImageURL"
                  :src="question.creatorImageURL"
                  :alt="question.creatorName || 'Автор'"
                  class="avatar"
                />

                <div
                  v-else
                  class="avatar avatar-placeholder"
                >
                  {{ creatorInitial }}
                </div>

              </div>


              <div class="author-info">

                <span class="author-name">
                  {{
                    question.creatorName
                    || "Пользователь"
                  }}
                </span>

                <span
                  v-if="question.creatorSpecifics"
                  class="author-role"
                >
                  {{ question.creatorSpecifics }}
                </span>

              </div>


              <time
                v-if="question.dateOfCreate"
                class="question-date"
              >
                {{ formatDate(question.dateOfCreate) }}
              </time>

            </div>

          </article>


          <!-- ===================================================
               ANSWERS HEADER
          ==================================================== -->

          <div class="answers-heading">

            <div>

              <span class="section-label">
                Ответы
              </span>

              <h3>
                Обсуждение
              </h3>

            </div>


            <div class="answers-counter">
              {{ localAnswers.length }}
            </div>

          </div>


          <!-- ===================================================
               EMPTY ANSWERS
          ==================================================== -->

          <div
            v-if="localAnswers.length === 0"
            class="answers-empty"
          >

            <div class="answers-empty-icon">

              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M6 6H18C19.66 6 21 7.34 21 9V14C21 15.66 19.66 17 18 17H12L7 20V17H6C4.34 17 3 15.66 3 14V9C3 7.34 4.34 6 6 6Z"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <path
                  d="M8 10H16"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />

                <path
                  d="M8 13.5H13"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>

            </div>


            <strong>
              Ответов пока нет
            </strong>

            <span>
              Можно стать первым участником обсуждения.
            </span>

          </div>


          <!-- ===================================================
               ANSWERS
          ==================================================== -->

          <div
            v-else
            class="answers-list"
          >

            <article
              v-for="(answer, index) in localAnswers"
              :key="
                answer.id
                || answer.answerId
                || index
              "
              class="answer-card"
            >

              <div class="answer-number">
                {{ String(index + 1).padStart(2, "0") }}
              </div>


              <div class="answer-content">

                <p class="answer-text">
                  {{
                    answer.answer
                    || "Ответ отсутствует."
                  }}
                </p>


                <div class="answer-author">

                  <div class="answer-avatar-wrapper">

                    <img
                      v-if="answer.responderImageURL"
                      :src="answer.responderImageURL"
                      :alt="answer.responderName || 'Автор ответа'"
                      class="avatar answer-avatar"
                    />

                    <div
                      v-else
                      class="avatar answer-avatar avatar-placeholder"
                    >
                      {{
                        personInitial(
                          answer.responderName
                        )
                      }}
                    </div>

                  </div>


                  <div class="author-info">

                    <span class="author-name">
                      {{
                        answer.responderName
                        || "Пользователь"
                      }}
                    </span>

                    <span
                      v-if="answer.responderSpecifics"
                      class="author-role"
                    >
                      {{ answer.responderSpecifics }}
                    </span>

                  </div>

                </div>

              </div>

            </article>

          </div>

        </div>


        <!-- =====================================================
             COMPOSER
        ====================================================== -->

        <footer class="comment-composer">

          <div class="composer-heading">

            <div>

              <span class="composer-label">
                Ваш ответ
              </span>

              <span class="composer-hint">
                Поделитесь опытом или мнением
              </span>

            </div>


            <span
              class="characters-count"
              :class="{
                limit:
                  commentLength >= maxCommentLength
              }"
            >
              {{ commentLength }}/{{ maxCommentLength }}
            </span>

          </div>


          <div
            class="comment-field"
            :class="{
              focused: isInputFocused,
              error: Boolean(sendError)
            }"
          >

            <textarea
              v-model="newComment"
              class="comment-input"
              placeholder="Напишите свой ответ..."
              rows="3"
              :maxlength="maxCommentLength"
              :disabled="isSending"
              @focus="isInputFocused = true"
              @blur="isInputFocused = false"
              @keydown.meta.enter.prevent="submitComment"
              @keydown.ctrl.enter.prevent="submitComment"
            ></textarea>


            <div class="comment-field-footer">

              <span class="keyboard-hint">
                ⌘ Enter — отправить
              </span>


              <button
                type="button"
                class="send-button"
                :disabled="
                  !canSend
                  || isSending
                "
                @click="submitComment"
              >

                <span
                  v-if="isSending"
                  class="button-spinner"
                ></span>


                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 12L20 5L15 20L11 14L4 12Z"
                    stroke="currentColor"
                    stroke-width="1.65"
                    stroke-linejoin="round"
                  />

                  <path
                    d="M11 14L20 5"
                    stroke="currentColor"
                    stroke-width="1.65"
                    stroke-linecap="round"
                  />
                </svg>


                <span>
                  {{
                    isSending
                    ? "Отправляем..."
                    : "Ответить"
                  }}
                </span>

              </button>

            </div>

          </div>


          <Transition name="error">

            <div
              v-if="sendError"
              class="send-error"
            >
              {{ sendError }}
            </div>

          </Transition>

        </footer>

      </section>

    </div>

  </Teleport>
</template>


<script setup>

import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";

import QuestionService
  from "../api/QuestionService.js";


/* ============================================================
   PROPS
============================================================ */

const props =
  defineProps({
    question: {
      type: Object,
      required: true
    }
  });


/* ============================================================
   EMITS
============================================================ */

const emit =
  defineEmits([
    "close",
    "answer-added"
  ]);


/* ============================================================
   STATE
============================================================ */

const newComment =
  ref("");

const isSending =
  ref(false);

const isInputFocused =
  ref(false);

const sendError =
  ref("");


const maxCommentLength =
  1200;


/*
 * Если answers уже есть,
 * сохраняем ту же ссылку на массив,
 * чтобы после unshift карточка вопроса
 * тоже увидела новый ответ.
 */
const localAnswers =
  ref(
    Array.isArray(
      props.question?.answers
    )
      ? props.question.answers
      : []
  );


/* ============================================================
   COMPUTED
============================================================ */

const commentLength =
  computed(
    () =>
      newComment.value.length
  );


const canSend =
  computed(
    () =>
      Boolean(
        newComment.value.trim()
      )
  );


const creatorInitial =
  computed(
    () =>
      personInitial(
        props.question?.creatorName
      )
  );


const answersCountTitle =
  computed(() => {

    const count =
      localAnswers.value.length;

    const lastTwo =
      count % 100;

    const lastOne =
      count % 10;


    if (
      lastTwo >= 11
      && lastTwo <= 19
    ) {
      return `${count} ответов`;
    }


    if (
      lastOne === 1
    ) {
      return `${count} ответ`;
    }


    if (
      lastOne >= 2
      && lastOne <= 4
    ) {
      return `${count} ответа`;
    }


    return `${count} ответов`;

  });


/* ============================================================
   CLOSE
============================================================ */

function close() {
  emit("close");
}


/* ============================================================
   ESCAPE
============================================================ */

function handleKeydown(event) {

  if (
    event.key === "Escape"
  ) {
    close();
  }

}


/* ============================================================
   BODY SCROLL
============================================================ */

onMounted(() => {

  window.addEventListener(
    "keydown",
    handleKeydown
  );


  /*
   * Пока открыта модалка,
   * запрещаем скролл страницы сзади.
   */
  document.body.style.overflow =
    "hidden";

});


onBeforeUnmount(() => {

  window.removeEventListener(
    "keydown",
    handleKeydown
  );


  document.body.style.overflow =
    "";

});


/* ============================================================
   DATE
============================================================ */

function formatDate(dateStr) {

  if (!dateStr) {
    return "";
  }


  const date =
    new Date(dateStr);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }


  return new Intl.DateTimeFormat(
    "ru-RU",
    {
      day: "2-digit",
      month: "long",
      year: "numeric"
    }
  ).format(date);

}


/* ============================================================
   INITIAL
============================================================ */

function personInitial(name) {

  if (!name) {
    return "?";
  }


  return String(name)
    .trim()
    .charAt(0)
    .toUpperCase();

}


/* ============================================================
   SUBMIT COMMENT
============================================================ */

async function submitComment() {

  const text =
    newComment.value.trim();


  if (
    !text
    || isSending.value
  ) {
    return;
  }


  sendError.value =
    "";


  try {

    isSending.value =
      true;


    const response =
      await QuestionService
        .createAnswer(
          props.question.id,
          text
        );


    const createdAnswer =
      response?.answer;


    if (!createdAnswer) {

      throw new Error(
        "Сервер не вернул созданный ответ"
      );

    }


    /*
     * Новый ответ показываем первым.
     */
    localAnswers.value.unshift(
      createdAnswer
    );


    /*
     * Если у question изначально
     * answers не было, сохраняем массив.
     */
    if (
      !Array.isArray(
        props.question.answers
      )
    ) {

      props.question.answers =
        localAnswers.value;

    }


    emit(
      "answer-added",
      createdAnswer
    );


    newComment.value =
      "";


  } catch (error) {

    console.error(
      "❌ Ошибка при отправке комментария:",
      error?.message
      || error
    );


    sendError.value =
      error?.response?.data?.error
      || error?.message
      || "Не удалось отправить ответ";


  } finally {

    isSending.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   OVERLAY
============================================================ */

.question-modal-overlay {
  position: fixed;

  inset: 0;

  z-index: 10000;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    28px;

  background:
    rgba(
      16,
      22,
      32,
      0.46
    );

  backdrop-filter:
    blur(10px);

  -webkit-backdrop-filter:
    blur(10px);

  animation:
    overlayAppear
    0.2s ease;
}


@keyframes overlayAppear {

  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }

}


/* ============================================================
   MODAL
============================================================ */

.question-modal {
  width:
    min(
      760px,
      100%
    );

  max-height:
    min(
      860px,
      calc(100dvh - 56px)
    );

  display: flex;
  flex-direction: column;

  overflow: hidden;

  color: #303640;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.985),
      rgba(248, 250, 253, 0.98)
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.7
    );

  border-radius: 28px;

  box-shadow:
    0 32px 90px
    rgba(
      12,
      25,
      45,
      0.26
    );

  animation:
    modalAppear
    0.34s
    cubic-bezier(
      0.22,
      1,
      0.36,
      1
    );
}


@keyframes modalAppear {

  from {
    opacity: 0;

    transform:
      translateY(16px)
      scale(0.975);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }

}


/* ============================================================
   HEADER
============================================================ */

.modal-header {
  min-height: 88px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 24px;

  box-sizing: border-box;

  padding:
    20px
    22px
    18px
    26px;

  flex-shrink: 0;

  background:
    rgba(
      255,
      255,
      255,
      0.78
    );

  border-bottom:
    1px solid
    rgba(
      34,
      52,
      77,
      0.065
    );

  backdrop-filter:
    blur(20px);

  -webkit-backdrop-filter:
    blur(20px);
}


.header-copy {
  min-width: 0;
}


.header-label {
  display: flex;
  align-items: center;

  gap: 6px;

  margin-bottom: 5px;

  color: #8190a3;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing:
    0.08em;

  text-transform:
    uppercase;
}


.header-label-dot {
  width: 5px;
  height: 5px;

  background:
    #588ee0;

  border-radius:
    50%;

  box-shadow:
    0 0 0 3px
    rgba(
      88,
      142,
      224,
      0.10
    );
}


.modal-title {
  max-width: 580px;

  margin: 0;

  overflow: hidden;

  color: #292f38;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;

  line-height: 1.25;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.header-meta {
  margin-top: 4px;

  color: #969da7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   CLOSE
============================================================ */

.close-button {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #838b97;

  background:
    rgba(
      50,
      68,
      93,
      0.045
    );

  border:
    1px solid
    rgba(
      37,
      55,
      82,
      0.055
    );

  border-radius: 12px;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background-color 0.18s ease,
    transform 0.18s ease;
}


.close-button:hover {
  color: #38414d;

  background:
    rgba(
      55,
      75,
      105,
      0.075
    );

  transform:
    rotate(4deg);
}


.close-button svg {
  width: 18px;
  height: 18px;
}


/* ============================================================
   SCROLL
============================================================ */

.modal-scroll {
  min-height: 0;

  flex: 1;

  overflow-y: auto;
  overflow-x: hidden;

  padding:
    22px
    25px
    25px;

  scrollbar-width:
    none;

  -webkit-overflow-scrolling:
    touch;
}


.modal-scroll::-webkit-scrollbar {
  display: none;
}


/* ============================================================
   QUESTION
============================================================ */

.question-main-card {
  position: relative;

  overflow: hidden;

  padding:
    20px;

  background:
    linear-gradient(
      145deg,
      #f7faff,
      #f6f7fc
    );

  border:
    1px solid
    rgba(
      60,
      99,
      159,
      0.075
    );

  border-radius: 21px;
}


.question-main-card::before {
  content: "";

  position: absolute;

  top: -110px;
  right: -90px;

  width: 240px;
  height: 240px;

  pointer-events: none;

  background:
    radial-gradient(
      circle,
      rgba(
        87,
        137,
        218,
        0.10
      ),
      transparent 68%
    );
}


.question-accent {
  position: absolute;

  top: 0;
  left: 25px;
  right: 25px;

  height: 3px;

  background:
    linear-gradient(
      90deg,
      transparent,
      #5c88d9,
      #716bd8,
      transparent
    );

  border-radius:
    0
    0
    10px
    10px;

  opacity: 0.78;
}


.question-main-label {
  position: relative;

  display: flex;
  align-items: center;

  gap: 7px;

  color: #7183a1;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing:
    0.05em;

  text-transform:
    uppercase;
}


.question-icon {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #618bd0;

  background:
    rgba(
      75,
      126,
      207,
      0.075
    );

  border-radius: 9px;
}


.question-icon svg {
  width: 15px;
  height: 15px;
}


.question-text {
  position: relative;

  margin:
    16px
    0
    19px;

  color: #3f4651;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;

  line-height: 1.7;
}


/* ============================================================
   AUTHOR
============================================================ */

.author-row,
.answer-author {
  display: flex;
  align-items: center;

  gap: 10px;
}


.avatar-wrapper,
.answer-avatar-wrapper {
  width: 38px;
  height: 38px;

  flex-shrink: 0;
}


.avatar {
  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  object-fit: cover;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.92
    );

  border-radius: 12px;

  box-shadow:
    0 4px 12px
    rgba(
      35,
      55,
      85,
      0.10
    );
}


.avatar-placeholder {
  color: #5877aa;

  background:
    linear-gradient(
      145deg,
      #e7eef9,
      #eef2f8
    );

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;
}


.author-info {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.author-name {
  overflow: hidden;

  color: #444b55;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.author-role {
  overflow: hidden;

  color: #9299a3;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.question-date {
  margin-left: auto;

  flex-shrink: 0;

  color: #a0a6af;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   ANSWERS HEADING
============================================================ */

.answers-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 20px;

  margin:
    26px
    2px
    13px;
}


.section-label {
  display: block;

  margin-bottom: 3px;

  color: #9ca3ad;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing:
    0.07em;

  text-transform:
    uppercase;
}


.answers-heading h3 {
  margin: 0;

  color: #363d47;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 14px;
}


.answers-counter {
  min-width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    0
    8px;

  color: #6882ae;

  background:
    rgba(
      71,
      112,
      181,
      0.065
    );

  border-radius: 9px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   ANSWERS EMPTY
============================================================ */

.answers-empty {
  min-height: 145px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 22px;

  text-align: center;

  background:
    rgba(
      244,
      246,
      249,
      0.72
    );

  border:
    1px dashed
    rgba(
      46,
      68,
      99,
      0.10
    );

  border-radius: 18px;
}


.answers-empty-icon {
  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 10px;

  color: #8796ab;

  background:
    #ffffff;

  border-radius: 12px;

  box-shadow:
    0 5px 15px
    rgba(
      25,
      45,
      75,
      0.055
    );
}


.answers-empty-icon svg {
  width: 19px;
  height: 19px;
}


.answers-empty strong {
  color: #555c66;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.answers-empty span {
  margin-top: 4px;

  color: #969da7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   ANSWERS
============================================================ */

.answers-list {
  display: flex;
  flex-direction: column;

  gap: 10px;
}


.answer-card {
  position: relative;

  display: flex;

  gap: 12px;

  padding:
    15px;

  background:
    rgba(
      248,
      249,
      251,
      0.92
    );

  border:
    1px solid
    rgba(
      35,
      54,
      80,
      0.055
    );

  border-radius: 17px;

  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    transform 0.18s ease;
}


.answer-card:hover {
  transform:
    translateY(-1px);

  background: #ffffff;

  border-color:
    rgba(
      72,
      119,
      192,
      0.12
    );
}


.answer-number {
  width: 25px;
  height: 25px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #96a0af;

  background:
    rgba(
      69,
      91,
      122,
      0.045
    );

  border-radius: 8px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 6px;
}


.answer-content {
  min-width: 0;

  flex: 1;
}


.answer-text {
  margin:
    1px
    0
    13px;

  color: #59616c;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;

  line-height: 1.6;
}


.answer-avatar-wrapper,
.answer-avatar {
  width: 31px;
  height: 31px;
}


.answer-avatar {
  border-radius: 10px;
}


/* ============================================================
   COMPOSER
============================================================ */

.comment-composer {
  flex-shrink: 0;

  box-sizing: border-box;

  padding:
    17px
    24px
    20px;

  background:
    rgba(
      255,
      255,
      255,
      0.90
    );

  border-top:
    1px solid
    rgba(
      31,
      48,
      72,
      0.065
    );

  backdrop-filter:
    blur(22px);

  -webkit-backdrop-filter:
    blur(22px);
}


.composer-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 20px;

  margin-bottom: 9px;
}


.composer-heading > div {
  display: flex;
  flex-direction: column;

  gap: 2px;
}


.composer-label {
  color: #454c56;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.composer-hint {
  color: #9aA1ab;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.characters-count {
  color: #a0a7b0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.characters-count.limit {
  color: #d66f73;
}


/* ============================================================
   FIELD
============================================================ */

.comment-field {
  overflow: hidden;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(
      32,
      52,
      80,
      0.075
    );

  border-radius: 16px;

  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}


.comment-field.focused {
  background:
    #ffffff;

  border-color:
    rgba(
      70,
      123,
      207,
      0.24
    );

  box-shadow:
    0 0 0 4px
    rgba(
      70,
      123,
      207,
      0.055
    );
}


.comment-field.error {
  border-color:
    rgba(
      208,
      86,
      94,
      0.24
    );
}


.comment-input {
  width: 100%;
  min-height: 72px;

  display: block;

  box-sizing: border-box;

  padding:
    13px
    14px
    8px;

  resize: none;

  color: #424952;

  background:
    transparent;

  border: 0;
  outline: 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.55;
}


.comment-input::placeholder {
  color: #a5abb4;
}


.comment-field-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 14px;

  padding:
    7px
    8px
    8px
    14px;
}


.keyboard-hint {
  color: #a3a9b2;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 6px;
}


/* ============================================================
   SEND
============================================================ */

.send-button {
  min-width: 105px;
  height: 34px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  padding:
    0
    12px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #4e88dc,
      #6574de
    );

  border: 0;

  border-radius: 10px;

  cursor: pointer;

  box-shadow:
    0 7px 16px
    rgba(
      69,
      103,
      191,
      0.19
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}


.send-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 10px 20px
    rgba(
      69,
      103,
      191,
      0.25
    );
}


.send-button:disabled {
  cursor: default;

  opacity: 0.48;

  box-shadow: none;
}


.send-button svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   SPINNER
============================================================ */

.button-spinner {
  width: 12px;
  height: 12px;

  box-sizing: border-box;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.32
    );

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}


@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   ERROR
============================================================ */

.send-error {
  margin:
    7px
    3px
    0;

  color: #c75a62;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.error-enter-active,
.error-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}


.error-enter-from,
.error-leave-to {
  opacity: 0;

  transform:
    translateY(-3px);
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.question-modal-overlay {
  background:
    rgba(
      4,
      6,
      10,
      0.62
    );
}


html[data-theme="dark"]
.question-modal {
  color: #edf0f4;

  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0 34px 100px
    rgba(
      0,
      0,
      0,
      0.48
    );
}


html[data-theme="dark"]
.modal-header,
html[data-theme="dark"]
.comment-composer {
  background:
    rgba(
      31,
      33,
      39,
      0.92
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.modal-title {
  color: #f0f1f4;
}


html[data-theme="dark"]
.header-meta,
html[data-theme="dark"]
.header-label {
  color: #767e89;
}


html[data-theme="dark"]
.close-button {
  color: #939aa5;

  background:
    rgba(
      255,
      255,
      255,
      0.04
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.05
    );
}


html[data-theme="dark"]
.close-button:hover {
  color: #ffffff;

  background:
    rgba(
      255,
      255,
      255,
      0.07
    );
}


html[data-theme="dark"]
.question-main-card {
  background:
    linear-gradient(
      145deg,
      #292c33,
      #27292f
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.question-text {
  color: #c4c8cf;
}


html[data-theme="dark"]
.author-name {
  color: #dedfe2;
}


html[data-theme="dark"]
.author-role,
html[data-theme="dark"]
.question-date {
  color: #7f8690;
}


html[data-theme="dark"]
.avatar {
  border-color: #292c32;
}


html[data-theme="dark"]
.avatar-placeholder {
  color: #a9c2eb;

  background:
    #343943;
}


html[data-theme="dark"]
.answers-heading h3 {
  color: #e3e5e8;
}


html[data-theme="dark"]
.section-label {
  color: #727984;
}


html[data-theme="dark"]
.answers-counter {
  color: #a4bce5;

  background:
    rgba(
      84,
      125,
      194,
      0.09
    );
}


html[data-theme="dark"]
.answers-empty {
  background:
    rgba(
      255,
      255,
      255,
      0.025
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


html[data-theme="dark"]
.answers-empty-icon {
  color: #8d97a6;

  background:
    #2d3037;
}


html[data-theme="dark"]
.answers-empty strong {
  color: #d8dade;
}


html[data-theme="dark"]
.answers-empty span {
  color: #777f8a;
}


html[data-theme="dark"]
.answer-card {
  background:
    rgba(
      255,
      255,
      255,
      0.025
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.answer-card:hover {
  background:
    rgba(
      255,
      255,
      255,
      0.04
    );

  border-color:
    rgba(
      104,
      145,
      212,
      0.13
    );
}


html[data-theme="dark"]
.answer-number {
  color: #7d8590;

  background:
    rgba(
      255,
      255,
      255,
      0.04
    );
}


html[data-theme="dark"]
.answer-text {
  color: #aeb3bc;
}


html[data-theme="dark"]
.composer-label {
  color: #d9dbdf;
}


html[data-theme="dark"]
.composer-hint,
html[data-theme="dark"]
.characters-count {
  color: #727984;
}


html[data-theme="dark"]
.comment-field {
  background:
    #282b31;

  border-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.comment-field.focused {
  background:
    #2c2f36;

  border-color:
    rgba(
      101,
      145,
      216,
      0.23
    );

  box-shadow:
    0 0 0 4px
    rgba(
      86,
      130,
      205,
      0.055
    );
}


html[data-theme="dark"]
.comment-input {
  color: #e0e2e5;
}


html[data-theme="dark"]
.comment-input::placeholder {
  color: #737a85;
}


html[data-theme="dark"]
.keyboard-hint {
  color: #666d78;
}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 820px) {

  .question-modal-overlay {
    padding:
      18px;
  }


  .question-modal {
    max-height:
      calc(
        100dvh - 36px
      );
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {

  .question-modal-overlay {
    align-items: center;

    padding:
      10px;
  }


  .question-modal {
    width: 100%;

    height:
      calc(
        100dvh - 20px
      );

    max-height: none;

    border-radius: 23px;
  }


  .modal-header {
    min-height: 77px;

    padding:
      15px
      14px
      14px
      17px;
  }


  .modal-title {
    max-width:
      calc(
        100vw - 110px
      );

    font-size: 16px;
  }


  .close-button {
    width: 36px;
    height: 36px;
  }


  .modal-scroll {
    padding:
      15px
      13px
      18px;
  }


  .question-main-card {
    padding:
      17px;

    border-radius: 18px;
  }


  .question-text {
    margin-top: 14px;

    font-size: 12px;
  }


  .question-date {
    display: none;
  }


  .answers-heading {
    margin-top: 22px;
  }


  .answer-card {
    gap: 9px;

    padding:
      13px;

    border-radius: 15px;
  }


  .answer-number {
    width: 23px;
    height: 23px;
  }


  .answer-text {
    font-size: 10px;
  }


  .comment-composer {
    padding:
      12px
      12px
      calc(
        13px
        + env(
          safe-area-inset-bottom
        )
      );
  }


  .comment-field {
    border-radius: 14px;
  }


  .comment-input {
    min-height: 64px;

    font-size: 11px;
  }


  .keyboard-hint {
    display: none;
  }


  .comment-field-footer {
    justify-content:
      flex-end;

    padding-left: 8px;
  }


  .send-button {
    min-width: 112px;
    height: 36px;
  }

}


/* ============================================================
   SMALL MOBILE
============================================================ */

@media (max-width: 390px) {

  .question-modal-overlay {
    padding:
      6px;
  }


  .question-modal {
    height:
      calc(
        100dvh - 12px
      );

    border-radius: 20px;
  }


  .question-main-card {
    padding:
      15px;
  }


  .author-role {
    max-width: 175px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion:
  reduce
) {

  .question-modal-overlay,
  .question-modal,
  .answer-card,
  .close-button,
  .send-button,
  .error-enter-active,
  .error-leave-active {
    animation: none;
    transition: none;
  }

}

</style>